/**
 * <kr-faq-page> — live search and category navigation for the FAQ page section.
 *
 * The accordions themselves are native <details>/<summary> and need no JavaScript; this file
 * only adds the two optional enhancements, and is only requested when one of them is enabled.
 * Everything it touches degrades cleanly: without the script the search field is an inert
 * input and the nav links are ordinary in-page anchors (a <noscript> rule in the section puts
 * back the categories the markup ships hidden).
 *
 * The nav filters: exactly one category is shown at a time. The section renders that first
 * selection server-side, so this file only has to keep it in sync from here on.
 *
 * Refs:
 *   [data-kr-faq-search]     — the search input
 *   [data-kr-faq-status]     — visually hidden aria-live region for the result count
 *   [data-kr-faq-empty]      — "no results" message
 *   [data-kr-faq-group]      — one group of questions
 *   [data-kr-faq-category]   — a group that a nav pill selects (the leading group has none)
 *   [data-kr-faq-item]       — one <details>
 *   [data-kr-faq-question]   — question text inside an item
 *   [data-kr-faq-answer]     — answer body inside an item
 *   [data-kr-faq-nav-link]   — a category pill; its value is the id of the group it selects
 */

const SEARCH_DEBOUNCE_MS = 150;

/**
 * Lowercases and strips diacritics so "expedition" matches "Expédition".
 *
 * @param {string} value
 * @returns {string}
 */
function normalize(value) {
  return value
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}

class KrFaqPage extends HTMLElement {
  /** @type {AbortController | undefined} */
  #controller;
  /** @type {number | undefined} */
  #debounceId;
  /** @type {Array<{ item: HTMLDetailsElement, haystack: string, wasOpen: boolean }>} */
  #entries = [];
  /** @type {HTMLElement[]} */
  #groups = [];
  /** @type {Map<string, HTMLAnchorElement>} */
  #navLinks = new Map();
  /** The id of the group the nav currently shows; empty when there is no nav. */
  #activeCategory = '';

  connectedCallback() {
    this.#controller = new AbortController();
    const { signal } = this.#controller;

    this.#groups = [.../** @type {NodeListOf<HTMLElement>} */ (this.querySelectorAll('[data-kr-faq-group]'))];

    this.#entries = [
      .../** @type {NodeListOf<HTMLDetailsElement>} */ (this.querySelectorAll('[data-kr-faq-item]')),
    ].map((item) => ({
      item,
      haystack: normalize(
        `${item.querySelector('[data-kr-faq-question]')?.textContent ?? ''} ${
          item.querySelector('[data-kr-faq-answer]')?.textContent ?? ''
        }`
      ),
      wasOpen: item.open,
    }));

    // Before the search wiring: a restored query has to win over the category selection.
    this.#setUpNav(signal);

    const search = this.querySelector('[data-kr-faq-search]');
    if (search instanceof HTMLInputElement) {
      search.addEventListener('input', this.#handleSearchInput, { signal });
      // Browsers restore search input values across back/forward navigations.
      if (search.value) this.#applyFilter(search.value);
    }
  }

  disconnectedCallback() {
    this.#controller?.abort();
    if (this.#debounceId) clearTimeout(this.#debounceId);
    this.#entries = [];
    this.#groups = [];
    this.#navLinks.clear();
    this.#activeCategory = '';
  }

  /** @param {Event} event */
  #handleSearchInput = (event) => {
    const input = /** @type {HTMLInputElement} */ (event.currentTarget);
    if (this.#debounceId) clearTimeout(this.#debounceId);
    this.#debounceId = setTimeout(() => this.#applyFilter(input.value), SEARCH_DEBOUNCE_MS);
  };

  /** @param {string} rawQuery */
  #applyFilter(rawQuery) {
    const query = normalize(rawQuery);
    let matches = 0;

    for (const entry of this.#entries) {
      const isMatch = query === '' || entry.haystack.includes(query);
      entry.item.hidden = !isMatch;
      if (isMatch) matches += 1;

      if (query === '') {
        // Restore whatever the merchant configured once the field is cleared.
        entry.item.open = entry.wasOpen;
      } else {
        entry.item.open = isMatch;
      }
    }

    // A category whose questions are all filtered out shouldn't leave a stranded heading. While a
    // query is live the search spans every category (the nav is hidden below); clearing the field
    // hands control back to the selected pill.
    for (const group of this.#groups) {
      const hasVisible = group.querySelector('[data-kr-faq-item]:not([hidden])') !== null;
      const filteredOut =
        query === '' &&
        this.#activeCategory !== '' &&
        group.hasAttribute('data-kr-faq-category') &&
        group.id !== this.#activeCategory;
      group.hidden = !hasVisible || filteredOut;
    }

    const empty = this.querySelector('[data-kr-faq-empty]');
    if (empty instanceof HTMLElement) empty.hidden = query === '' || matches > 0;

    const status = this.querySelector('[data-kr-faq-status]');
    if (status instanceof HTMLElement) {
      status.textContent = query === '' ? '' : `${matches}`;
    }

    const nav = this.querySelector('.kr-faq-page__nav');
    if (nav instanceof HTMLElement) nav.hidden = query !== '';
  }

  /** @param {AbortSignal} signal */
  #setUpNav(signal) {
    const links = /** @type {NodeListOf<HTMLAnchorElement>} */ (
      this.querySelectorAll('[data-kr-faq-nav-link]')
    );
    if (links.length === 0) return;

    for (const link of links) {
      const id = link.dataset.krFaqNavLink ?? '';
      if (id) this.#navLinks.set(id, link);
      link.addEventListener('click', this.#handleNavClick, { signal });
    }

    // A deep link such as /pages/faq#KrFaqGroup-cat_returns opens on that category.
    const hashed = decodeURIComponent(window.location.hash.slice(1));
    const initial = this.#navLinks.has(hashed) ? hashed : this.#navLinks.keys().next().value;
    if (initial) this.#selectCategory(initial);

    // In the editor, picking a block whose category is filtered out would otherwise scroll the
    // merchant to nothing.
    if (window.Shopify?.designMode) {
      document.addEventListener('shopify:block:select', this.#handleBlockSelect, { signal });
    }
  }

  /** @param {Event} event */
  #handleBlockSelect = (event) => {
    if (!(event.target instanceof Element)) return;
    const group = event.target.closest('[data-kr-faq-category]');
    if (group instanceof HTMLElement && this.#navLinks.has(group.id)) this.#selectCategory(group.id);
  };

  /** @param {MouseEvent} event */
  #handleNavClick = (event) => {
    const link = /** @type {HTMLAnchorElement} */ (event.currentTarget);
    const id = link.dataset.krFaqNavLink ?? '';
    if (!this.#navLinks.has(id)) return;

    event.preventDefault();

    this.#selectCategory(id);
    history.replaceState(null, '', link.hash);
  };

  /**
   * Shows one category and marks its pill. The leading group — questions the merchant placed
   * before the first category — carries no [data-kr-faq-category] and stays visible throughout.
   *
   * @param {string} id
   */
  #selectCategory(id) {
    this.#activeCategory = id;

    for (const group of this.#groups) {
      if (group.hasAttribute('data-kr-faq-category')) group.hidden = group.id !== id;
    }

    for (const [linkId, link] of this.#navLinks) {
      const isActive = linkId === id;
      link.classList.toggle('kr-faq-page__nav-link--active', isActive);
      if (isActive) {
        link.setAttribute('aria-current', 'true');
      } else {
        link.removeAttribute('aria-current');
      }
    }
  }
}

if (!customElements.get('kr-faq-page')) {
  customElements.define('kr-faq-page', KrFaqPage);
}
